import { fetchWithRetry } from '@/utils/fetchWithRetry';

/**
 * Module-level access-token getter. In a SPA the Auth0 access token lives in
 * browser memory and is provided by the `useAuth0().getAccessTokenSilently`
 * hook. Since the API layer is plain functions (not hooks), `AuthBridge`
 * wires the live getter in once Auth0 has initialised.
 *
 * @see app/auth/AuthBridge.tsx
 */
let liveGetToken: (() => Promise<string>) | null = null;

// Resolves the first time `setTokenGetter` runs. API calls can fire before
// AuthBridge's wiring effect (e.g. the `useCurrentUser` query enables the
// instant Auth0 restores the session from the localStorage cache, before the
// effect that installs the getter has run — a race that's reliably lost on a
// cold-cache reload of a lazy route). Awaiting readiness makes an early call
// wait a beat for the getter instead of throwing — the old throwing stub was
// caught as a terminal auth failure and forced a spurious re-login.
let notifyGetterReady!: () => void;
const getterReady = new Promise<void>((resolve) => {
    notifyGetterReady = resolve;
});

const getToken = async (): Promise<string> => {
    if (!liveGetToken) await getterReady;
    // `liveGetToken` is always set once `getterReady` resolves.
    return liveGetToken!();
};

export function setTokenGetter(fn: () => Promise<string>): void {
    liveGetToken = fn;
    notifyGetterReady();
}

/**
 * Invoked when acquiring an access token fails terminally (refresh token
 * expired/revoked/absent, `login_required`, etc). `AuthBridge` wires this to
 * `loginWithRedirect` so the user is sent cleanly back through login rather
 * than hitting a generic query error / error boundary.
 */
let onAuthError: () => void = () => {};

export function setAuthErrorHandler(fn: () => void): void {
    onAuthError = fn;
}

/**
 * OAuth error codes from `getAccessTokenSilently` that mean the session is
 * genuinely unrecoverable and interactive re-login is the only way forward.
 * Everything else (network blip, timeout, a generic error with no code) is
 * treated as transient and must NOT log the user out — it surfaces as a normal
 * request error so the query can retry/recover.
 */
const TERMINAL_AUTH_ERROR_CODES = new Set([
    'login_required',
    'consent_required',
    'interaction_required',
    'mfa_required',
    'missing_refresh_token',
    'invalid_grant',
]);

function isTerminalAuthError(err: unknown): boolean {
    // auth0-spa-js errors carry the OAuth error code on `.error`.
    const code = (err as { error?: unknown } | null)?.error;
    return typeof code === 'string' && TERMINAL_AUTH_ERROR_CODES.has(code);
}

const API_URL = import.meta.env.VITE_PRO_BACKEND_API_URL;

export class ApiError extends Error {
    status: number;
    constructor(message: string, status: number) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
    }
}

type ApiInit = Omit<RequestInit, 'body'> & {
    /** JSON body — serialised automatically. Use `rawBody` for FormData etc. */
    body?: unknown;
    /** Pass through a body without JSON-serialising (e.g. FormData for uploads). */
    rawBody?: BodyInit;
};

/**
 * Authenticated fetch against the Pro backend. Attaches the Auth0 bearer token,
 * sets JSON headers (unless a raw body is supplied), and reuses the existing
 * retry behaviour. Returns the raw `Response` — callers parse as needed.
 */
export async function apiFetch(path: string, init: ApiInit = {}): Promise<Response> {
    let token: string;
    try {
        token = await getToken();
    } catch (err) {
        // Only a *terminal* auth error (expired/revoked/absent refresh token,
        // interactive login required, …) means the session is truly gone — send
        // the user back through login. A transient failure (network blip,
        // timeout) must not log them out; let it bubble as a normal request
        // error so React Query can retry.
        if (isTerminalAuthError(err)) onAuthError();
        throw err;
    }

    const { body, rawBody, headers, ...rest } = init;

    const finalHeaders = new Headers(headers);
    finalHeaders.set('Authorization', `Bearer ${token}`);

    let finalBody: BodyInit | undefined;
    if (rawBody !== undefined) {
        finalBody = rawBody; // let the browser set the Content-Type (e.g. multipart boundary)
    } else if (body !== undefined) {
        finalBody = JSON.stringify(body);
        if (!finalHeaders.has('Content-Type')) {
            finalHeaders.set('Content-Type', 'application/json');
        }
    }

    return fetchWithRetry(`${API_URL}${path}`, {
        ...rest,
        headers: finalHeaders,
        body: finalBody,
    });
}
